import * as React from 'react';
import { Device } from '@mentimeter/ragnar-device';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Light } from '@mentimeter/ragnar-ui/themes';
import {
  ModalBody,
  ModalContainer,
  ModalHeader,
} from '@mentimeter/ragnar-ui/modal';
import { SelectItem } from '@mentimeter/ragnar-ui/input/select-item';
import { Tabs } from '@mentimeter/ragnar-ui/tabs';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import type { TrackingContext } from '@mentimeter/http-clients';
import type { HelpTab } from './types/HelpTab';
import { Powerpoint } from './powerpoint';
import { MentiAcademy } from './menti-academy';
import { HelpCenter } from './help-center';
import { HowTo } from './how-to';
import { GettingStarted } from './getting-started';

export const HELP_TABS_MAP: Record<
  HelpTab,
  React.ComponentType<{ context: TrackingContext }>
> = {
  'Get started': GettingStarted,
  'How to': HowTo,
  'Help center': HelpCenter,
  'Menti Academy': MentiAcademy,
  Powerpoint,
};

interface HelpModalProps extends React.ComponentProps<typeof ModalContainer> {
  open: boolean;
  tabs: HelpTab[];
  context: TrackingContext;
  activeTab: HelpTab;
  onChangeActiveTab: (tab: HelpTab) => void;
}

export const HelpModal = ({
  open,
  tabs,
  context,
  activeTab,
  onChangeActiveTab,
  ...modalProps
}: HelpModalProps) => {
  const CurrentComponent = React.useMemo(
    () => HELP_TABS_MAP[activeTab],
    [activeTab],
  );

  const changeTab = (tab: HelpTab) => {
    if (tab === activeTab) return;
    onChangeActiveTab(tab);
  };

  // TODO: Remove this Light wrapper when mobile-menu is no longer themed with Primary
  return (
    <Light>
      <ModalContainer withPortal={false} topAligned {...modalProps}>
        <ModalHeader>
          <ScreenReaderOnly>Help</ScreenReaderOnly>
        </ModalHeader>
        <ModalBody
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'stretch',
            columnGap: '16px',
          }}
        >
          <Box mb="space8" width="100%">
            <Device.Match equals={1}>
              <SelectItem
                value={activeTab}
                onChange={(e) => changeTab(e.currentTarget.value as HelpTab)}
              >
                {tabs.map((tab) => (
                  <SelectItem.Option key={tab} value={tab}>
                    {tab}
                  </SelectItem.Option>
                ))}
              </SelectItem>
            </Device.Match>

            <Device.Match greaterThan={1}>
              <Tabs
                fullWidth={true}
                actions={tabs.map((tab) => ({
                  active: activeTab === tab,
                  onClick: () => changeTab(tab),
                  title: tab,
                  key: tab,
                }))}
              />
            </Device.Match>
          </Box>
          <Box
            flex="1 1 auto"
            width="100%"
            bg="bg"
            overflow="scroll"
            style={{ flexGrow: 1 }}
          >
            <CurrentComponent context={context} />
          </Box>
        </ModalBody>
      </ModalContainer>
    </Light>
  );
};
